"use client";

import { useState } from "react";

type RootCheckoutButtonProps = {
  productId: string;
  buttonText: string;
  className?: string;
};

const isAuthError = (message?: string | null) => {
  if (!message) {
    return false;
  }

  const normalized = message.toLowerCase();
  return (
    normalized.includes("no auth") ||
    normalized.includes("unauth") ||
    normalized.includes("sign-in") ||
    normalized.includes("sign in")
  );
};

const getSignInUrl = () => {
  const callbackUrl =
    typeof window === "undefined"
      ? "/"
      : `${window.location.pathname}${window.location.search}${window.location.hash}`;

  return `/auth/signin?callbackUrl=${encodeURIComponent(callbackUrl)}`;
};

export default function RootCheckoutButton({
  productId,
  buttonText,
  className,
}: RootCheckoutButtonProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const checkoutPaused = process.env.NEXT_PUBLIC_PAUSE_CHECKOUT === "true";

  const handleClick = async () => {
    if (checkoutPaused || isLoading) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch("/api/checkout", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          product_id: productId,
          currency: "usd",
          locale: "en",
        }),
      });

      const payload = await response.json().catch(() => null);

      if (response.status === 401 || isAuthError(payload?.message)) {
        window.location.href = getSignInUrl();
        return;
      }

      const checkoutUrl = payload?.data?.checkout_url;
      if (payload?.code === 0 && checkoutUrl) {
        window.location.href = checkoutUrl;
        return;
      }

      setError("Checkout is temporarily unavailable.");
    } catch {
      setError("Checkout is temporarily unavailable.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-start gap-2">
      <button
        type="button"
        className={className}
        disabled={checkoutPaused || isLoading}
        onClick={handleClick}
      >
        {checkoutPaused ? "Checkout unavailable" : isLoading ? "Loading..." : buttonText}
      </button>
      {error ? <p className="text-sm text-[#ffb5aa]">{error}</p> : null}
    </div>
  );
}
